/**
 * @fileoverview This file handles automated git commit message generation using AI. It uses `getDiffs` to analyze code changes and `streamChatCompletions` to generate commit messages. The `writeCommitMessage` function orchestrates the process of creating and committing changes.
 */

import { Message, Model } from '@iterator/shared-node/ai/llm_types'
import { getDiff, getDiffs } from 'git-diff-clean'
import { streamChatCompletions } from './ai/llm_api'
import { add, commit } from '@iterator/shared-node/util/git'
import { debug_block, error, print_ai, print_ai_stream } from '@iterator/shared-node/util/logging'
import { time } from '@iterator/shared-node/util/timing'

export async function writeCommitMessage(
  directory: string,
  model: Model,
  all: boolean = false,
): Promise<void> {
  const diffs = getDiffs(all, !all)
  const diffString = getDiff({
    includePlusMinus: true,
    includeColors: false,
    includeEmoji: false,
    includeFooter: false,
    includeUntracked: all,
    cached: !all,
  })

  if (!diffs || diffs.length === 0) {
    error('No changes to commit.')
    return
  }

  const system = 'You are an AI assistant that writes clear and concise git commit messages.'
  const conversation: Message[] = [
    {
      role: 'user',
      content:
        `Based on the following git diff, please write a concise and informative commit message. It should provide` +
        ` enough context to know what part of the code base is being modified. Please output only the message, not` +
        ` any other text.\n\n<diff>${diffString}</diff>`,
    },
  ]

  Array.from(new Set(diffs.map((diff) => diff.path))).forEach((path) => {
    print_ai(path)
  })
  print_ai('')
  const { message, timing } = await time('streamChatCompletions', async () =>
    streamChatCompletions(system, [], conversation, print_ai_stream, model),
  )
  print_ai('')

  if (all) {
    await add(directory)
  }
  await commit(message.content, directory)

  debug_block('Timing', timing)
}
